import { useEffect, useState } from "react";

import { eventEmitter } from "../eventEmitters/EventEmitters";
import { toastMessageError, toastMessageSuccess } from "../components/utilities/commonToastMessage";
import { shareApi } from "./shareStore";
import { DEFAULT_EXPIRY_DAYS, shareKey, shareLink, ShareSnapshot, shareState } from "./shareSnapshot";
import "./share.scss";

const when = (ms: number) => new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const whenAt = (ms: number) => new Date(ms).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const copyShareLink = async (token: string) => {
  const link = shareLink(window.location.origin, token);
  try { await navigator.clipboard.writeText(link); toastMessageSuccess("Link copied", "shareCopied"); } catch { toastMessageError(`Copy it from here: ${link}`, "shareCopyFailed"); }
};

/**
 * The replay clip links that have been made, each with its state (live, expired, turned off) and what can be done: copy, renew (the same address, counted from today) and turn off for good. ONE component, shown in the
 * Share dialog and in Settings, so the two can never disagree; it loads for itself and reloads whenever a link is made, renewed or turned off anywhere ("sharesChanged").
 */
const ShareLinksList = ({ empty }: { empty?: string }) => {
  const [shares, setShares] = useState<ShareSnapshot[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const refresh = () => shareApi.list().then((list) => { setShares(list); setFailed(false); setLoaded(true); }).catch(() => { setFailed(true); setLoaded(true); });
  useEffect(() => {
    refresh();
    eventEmitter.addListener("sharesChanged", refresh);

    return () => { eventEmitter.removeListener("sharesChanged", refresh); };
  }, []);
  if (failed) return <p className="share-list-empty" data-share-list-failed>Your replay clips could not be loaded. <button type="button" className="transparent-btn" onClick={refresh}>TRY AGAIN</button></p>;
  if (loaded && shares.length === 0) return empty ? <p className="share-list-empty" data-share-list-empty>{empty}</p> : null;
  if (shares.length === 0) return null;

  return (
    <div className="share-list" data-share-list>
      <div className="share-list-title">Your replay clip links</div>
      {shares.map((s) => {
        const state = shareState(s, Date.now());

        return (
          <div className="share-list-item" key={shareKey(s)} data-share-item={shareKey(s)} data-share-state={state}>
            <span className="share-list-name">{s.label || "Untitled"} <em>made {whenAt(s.createdAt)}</em><em>clip: {whenAt(s.startMs)}, {Math.round((s.endMs - s.startMs) / 60000)} min</em></span>
            {typeof s.views === "number" && <span className="share-list-state">{s.views} view{s.views === 1 ? "" : "s"}</span>}
            <span className="share-list-state">{state === "live" ? `until ${when(s.expiresAt)}` : state === "expired" ? "expired" : "turned off"}</span>
            {state !== "revoked" && (s.token ? <button type="button" className="transparent-btn" data-share-copy-item onClick={() => copyShareLink(s.token)}>COPY</button> : <span className="share-list-state" data-share-not-stored>link not stored on this device</span>)}
            {state !== "revoked" && <button type="button" className="transparent-btn" data-share-renew onClick={async () => { await shareApi.renew(shareKey(s), DEFAULT_EXPIRY_DAYS); }}>RENEW</button>}
            {state !== "revoked" && <button type="button" className="transparent-btn" data-share-revoke onClick={async () => { await shareApi.revoke(shareKey(s)); }}>TURN OFF</button>}
          </div>
        );
      })}
    </div>
  );
};

export default ShareLinksList;
